import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const stats = [
  { icon: 'ri-user-smile-line', value: 10000, suffix: '+', label: 'Happy Customers', color: 'from-orange-500 to-pink-600' },
  { icon: 'ri-store-2-line', value: 500, suffix: '+', label: 'Products', color: 'from-amber-500 to-orange-600' },
  { icon: 'ri-star-fill', value: 4.9, suffix: '★', label: 'Avg Rating', color: 'from-rose-500 to-pink-600' },
  { icon: 'ri-award-line', value: 5, suffix: ' Yrs', label: 'Excellence', color: 'from-red-500 to-rose-600' },
];

const features = [
  { icon: 'ri-shield-check-line', text: 'Premium Quality Products', color: 'from-orange-500 to-pink-600' },
  { icon: 'ri-truck-line', text: 'Fast & Reliable Delivery', color: 'from-amber-500 to-orange-600' },
  { icon: 'ri-lock-line', text: 'Secure Payment Options', color: 'from-rose-500 to-pink-600' },
  { icon: 'ri-refresh-line', text: 'Easy Returns & Exchanges', color: 'from-red-500 to-rose-600' },
];

function AnimatedCounter({ target, suffix }: { target: number; suffix: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 2000;
          const steps = 60;
          const increment = target / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref}>
      {target % 1 !== 0 ? count.toFixed(1) : count.toLocaleString()}{suffix}
    </span>
  );
}

export default function AboutUs() {
  const navigate = useNavigate();
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const [leftVisible, setLeftVisible] = useState(false);
  const [rightVisible, setRightVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.target === leftRef.current && e.isIntersecting) setLeftVisible(true);
          if (e.target === rightRef.current && e.isIntersecting) setRightVisible(true);
        });
      },
      { threshold: 0.15 }
    );
    if (leftRef.current) obs.observe(leftRef.current);
    if (rightRef.current) obs.observe(rightRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="about" className="py-24 relative overflow-hidden">
      {/* Vibrant background */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50" />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #f97316, #ec4899, transparent)', filter: 'blur(80px)' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Image Side */}
          <div
            ref={leftRef}
            className={`relative transition-all duration-1000 ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-16'}`}
          >
            {/* Decorative background blob */}
            <div
              className="absolute -top-8 -left-8 w-full h-full rounded-3xl opacity-30 animate-morph"
              style={{ background: 'linear-gradient(135deg, #f97316, #ec4899)', filter: 'blur(20px)' }}
            />

            <div className="relative w-full h-64 sm:h-96 lg:h-[560px] rounded-3xl overflow-hidden border-2 border-white/50"
              style={{ boxShadow: '0 0 40px rgba(249,115,22,0.2), 0 30px 60px rgba(0,0,0,0.15)' }}>
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20stylish%20e-commerce%20shopping%20scene%20with%20diverse%20happy%20customers%20browsing%20colorful%20products%20in%20bright%20contemporary%20store%20with%20clean%20minimalist%20design%20and%20warm%20inviting%20atmosphere%20showcasing%20quality%20merchandise&width=800&height=600&seq=about1&orientation=landscape"
                alt="About AiShopio"
                className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>

            {/* Floating stat cards - hidden on small screens to avoid overflow */}
            <div
              className="hidden sm:block absolute -bottom-6 -right-6 bg-white rounded-2xl p-4 sm:p-5 border border-orange-100 transition-all duration-300 hover:scale-105 cursor-default"
              style={{ boxShadow: '0 0 20px rgba(249,115,22,0.2), 0 10px 30px rgba(0,0,0,0.1)' }}
            >
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center rounded-xl"
                  style={{ background: 'linear-gradient(135deg, #f97316, #ec4899)' }}>
                  <i className="ri-trophy-line text-white text-lg sm:text-2xl"></i>
                </div>
                <div>
                  <div className="text-lg sm:text-2xl font-black gradient-text-fire">5 Years</div>
                  <div className="text-xs sm:text-sm text-gray-500">of Excellence</div>
                </div>
              </div>
            </div>

            <div
              className="hidden sm:block absolute -top-6 -right-6 bg-white rounded-2xl p-3 sm:p-4 border border-pink-100 transition-all duration-300 hover:scale-105 cursor-default animate-float"
              style={{ boxShadow: '0 0 20px rgba(236,72,153,0.2), 0 10px 30px rgba(0,0,0,0.1)' }}
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl"
                  style={{ background: 'linear-gradient(135deg, #ec4899, #f97316)' }}>
                  <i className="ri-star-fill text-white text-sm sm:text-lg"></i>
                </div>
                <div>
                  <div className="text-base sm:text-lg font-black gradient-text-sunset">4.9★</div>
                  <div className="text-xs text-gray-500">Top Rated</div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Side */}
          <div
            ref={rightRef}
            className={`transition-all duration-1000 delay-200 ${rightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'}`}
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-white text-sm font-bold mb-6"
              style={{ background: 'linear-gradient(135deg, #f97316, #ec4899)', boxShadow: '0 0 20px rgba(249,115,22,0.4)' }}>
              <i className="ri-information-line"></i>
              <span className="tracking-widest">ABOUT US</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 leading-tight">
              Your Trusted Partner for
              <span className="block mt-1 gradient-text-fire">
                Quality Shopping
              </span>
            </h2>

            <p className="text-gray-600 mb-4 leading-relaxed text-lg">
              At <strong className="text-orange-600">AiShopio</strong>, we're passionate about bringing you the finest selection of products across beauty, kitchen, health, and electronics.
            </p>
            <p className="text-gray-600 mb-8 leading-relaxed">
              With over <strong className="text-pink-600">10,000 satisfied customers</strong> and a carefully curated collection of <strong className="text-orange-600">500+ products</strong>, we've built a reputation for excellence, reliability, and exceptional customer service.
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className="group relative bg-white rounded-2xl p-4 border border-gray-100 transition-all duration-300 hover:scale-105 cursor-default overflow-hidden"
                  style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.06)', transitionDelay: `${i * 100}ms` }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${stat.color}`}>
                      <i className={`${stat.icon} text-white text-lg`}></i>
                    </div>
                    <div>
                      <div className={`text-xl font-black bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                        <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                      </div>
                      <div className="text-xs text-gray-500">{stat.label}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {features.map((item, i) => (
                <div
                  key={i}
                  className="group flex items-center gap-3 p-3 rounded-xl bg-white/60 border border-gray-100 transition-all duration-300 hover:scale-105 hover:border-orange-200 cursor-default"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg bg-gradient-to-br ${item.color} transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6`}>
                    <i className={`${item.icon} text-white`}></i>
                  </div>
                  <span className="text-gray-700 font-medium text-sm group-hover:text-orange-600 transition-colors duration-300">
                    {item.text}
                  </span>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate('/contact')}
              className="group relative px-10 py-4 rounded-full font-bold text-white text-lg overflow-hidden transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, #f97316, #ec4899)',
                boxShadow: '0 0 25px rgba(249,115,22,0.4), 0 10px 30px rgba(0,0,0,0.15)',
              }}
            >
              <span className="relative z-10 flex items-center gap-2">
                <i className="ri-information-line"></i>
                Learn More About Us
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
              </span>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
